import { Navigate, useParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Spinner,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { useAppSelector } from 'hooks';

import { InvoicingBillingPanel } from './components/invoicing-billing';
import { useInvoicingBilling } from './hooks';

const INVOICING_BILLING_FLAG = 'enable-invoicing-billing';

const InvoicingBillingView = () => {
  const { contactRef, rfqRef, quoteRef } = useParams<{
    contactRef: string;
    rfqRef: string;
    quoteRef: string;
  }>();
  const bgColor = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('gray.700', 'white');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');

  const isEnabled = useFeatureFlag(INVOICING_BILLING_FLAG);
  const flagsLoaded = useAppSelector(
    (state) => state.featureFlags.lastFetched !== null,
  );

  const {
    data: billing,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useInvoicingBilling(contactRef, rfqRef, quoteRef, isEnabled);

  if (!flagsLoaded) return null;

  if (!isEnabled) {
    return <Navigate to="/admin/home" replace />;
  }

  return (
    <Card
      bg={bgColor}
      boxShadow="0px 3px 10px rgba(112, 144, 176, 0.12)"
      borderRadius="15px"
      mb="20px"
      p="0"
    >
      <Box borderBottom="1px solid" borderColor={borderColor} p="20px">
        <Heading size="lg" color={textColor}>
          Billing Information
        </Heading>
        <Text fontSize="sm" color={muted} mt="1">
          {[
            `RFQ Ref # ${rfqRef}`,
            `Quote Ref # ${quoteRef}`,
            billing?.product_name,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Text>
      </Box>

      <Box p="20px">
        {isLoading ? (
          <Center py={10} flexDirection="column" gap="3">
            <Spinner size="xl" color="brand.500" />
            <Text fontSize="sm" color={muted}>
              Reading billing details from Acturis…
            </Text>
          </Center>
        ) : error ? (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Flex
              flex="1"
              align="center"
              justify="space-between"
              gap="3"
              wrap="wrap"
            >
              <AlertDescription>
                Billing details could not be loaded from Acturis.
                {error.message && (
                  <Text as="span" display="block" fontSize="sm">
                    {error.message}
                  </Text>
                )}
              </AlertDescription>
              <Button
                size="sm"
                onClick={() => refetch()}
                isLoading={isFetching}
              >
                Retry
              </Button>
            </Flex>
          </Alert>
        ) : billing ? (
          <InvoicingBillingPanel billing={billing} />
        ) : null}
      </Box>
    </Card>
  );
};

export default InvoicingBillingView;
